import React, { useState, useRef, useEffect } from 'react';

const CropTool = ({ imageUrl, initialCrop, onCropChange, rotation = 0 }) => {
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgDimensions, setImgDimensions] = useState({ width: 0, height: 0 });
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
  const [crop, setCrop] = useState(initialCrop || { x: 10, y: 10, width: 80, height: 80 }); // Percentage values
  const [draggingCorner, setDraggingCorner] = useState(null);
  const [draggingBox, setDraggingBox] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Create a canvas for rotation so we can get the dimensions after rotation
  useEffect(() => {
    if (imageRef.current && imageRef.current.complete) {
      handleImageLoad();
    }
  }, [rotation]); // Re-run when rotation changes

  // Handle image load to set dimensions
  const handleImageLoad = () => {
    if (imageRef.current) {
      const img = imageRef.current;
      
      // Create a temporary canvas to handle the rotation and get correct dimensions
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas dimensions based on rotation
      let width = img.naturalWidth;
      let height = img.naturalHeight;
      
      // If rotation is 90 or 270 degrees, swap width and height
      if (Math.abs(rotation) === 90 || Math.abs(rotation) === 270) {
        canvas.width = height;
        canvas.height = width;
      } else {
        canvas.width = width;
        canvas.height = height;
      }
      
      // Translate and rotate the canvas
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.translate(-width / 2, -height / 2);
      
      // Store the rotated dimensions
      setImgDimensions({
        width: canvas.width,
        height: canvas.height
      });
      
      // Initialize crop if not already set
      if (!initialCrop) {
        setCrop({
          x: 10, // 10% from left
          y: 10, // 10% from top
          width: 80, // 80% of image width
          height: 80 // 80% of image height
        });
      }
      
      setImgLoaded(true);
    }
  };

  // Update container dimensions on resize
  useEffect(() => {
    const updateContainerDimensions = () => {
      if (containerRef.current) {
        const { offsetWidth, offsetHeight } = containerRef.current;
        setContainerDimensions({ width: offsetWidth, height: offsetHeight });
      }
    };

    updateContainerDimensions();
    window.addEventListener('resize', updateContainerDimensions);
    return () => window.removeEventListener('resize', updateContainerDimensions);
  }, []);

  // Notify parent component when crop changes
  useEffect(() => {
    if (imgLoaded) {
      onCropChange(crop);
    }
  }, [crop, imgLoaded, onCropChange]);

  // Convert percentage to pixels for display
  const cropToPixels = () => {
    // Get the current size of the displayed image (which may be scaled)
    const displayedImg = imageRef.current;
    
    if (!displayedImg) return { x: 0, y: 0, width: 0, height: 0 };
    
    const displayWidth = displayedImg.width;
    const displayHeight = displayedImg.height;
    
    return {
      x: (crop.x / 100) * displayWidth,
      y: (crop.y / 100) * displayHeight,
      width: (crop.width / 100) * displayWidth,
      height: (crop.height / 100) * displayHeight
    };
  };

  // Convert pixel position to percentage
  const pixelToPercent = (x, y) => {
    const displayedImg = imageRef.current;
    
    if (!displayedImg) return { x: 0, y: 0 };
    
    const displayWidth = displayedImg.width;
    const displayHeight = displayedImg.height;
    
    return {
      x: (x / displayWidth) * 100,
      y: (y / displayHeight) * 100
    };
  };

  // Handle mouse down for corners and box
  const handleMouseDown = (event, corner) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (corner) {
      setDraggingCorner(corner);
    } else {
      setDraggingBox(true);
    }
    
    const { clientX, clientY } = event.touches ? event.touches[0] : event;
    setDragStart({ x: clientX, y: clientY });
  };

  // Handle movement of crop box or corners
  const handleMouseMove = (event) => {
    if (!draggingCorner && !draggingBox) return;
    
    event.preventDefault();
    const { clientX, clientY } = event.touches ? event.touches[0] : event;
    const { x: startX, y: startY } = dragStart;
    const deltaX = clientX - startX;
    const deltaY = clientY - startY;
    
    // Get the displayed image dimensions
    const displayedImg = imageRef.current;
    
    if (!displayedImg) return;
    
    const displayWidth = displayedImg.width;
    const displayHeight = displayedImg.height;
    
    // Convert pixel movement to percentage
    const deltaPercentX = (deltaX / displayWidth) * 100;
    const deltaPercentY = (deltaY / displayHeight) * 100;

    // Create new crop state
    const newCrop = { ...crop };

    if (draggingBox) {
      // Move the entire box, ensuring it stays within bounds
      newCrop.x = Math.max(0, Math.min(100 - newCrop.width, crop.x + deltaPercentX));
      newCrop.y = Math.max(0, Math.min(100 - newCrop.height, crop.y + deltaPercentY));
    } else if (draggingCorner) {
      // Adjust corner position
      switch (draggingCorner) {
        case 'topLeft':
          // Adjust x, y, width, height
          const newWidth1 = Math.max(10, crop.width - deltaPercentX);
          const newHeight1 = Math.max(10, crop.height - deltaPercentY);
          
          newCrop.x = Math.min(crop.x + deltaPercentX, crop.x + crop.width - 10);
          newCrop.y = Math.min(crop.y + deltaPercentY, crop.y + crop.height - 10);
          newCrop.width = Math.min(100 - newCrop.x, newWidth1);
          newCrop.height = Math.min(100 - newCrop.y, newHeight1);
          break;
          
        case 'topRight':
          // Adjust y, width, height
          const newWidth2 = Math.max(10, crop.width + deltaPercentX);
          const newHeight2 = Math.max(10, crop.height - deltaPercentY);
          
          newCrop.y = Math.min(crop.y + deltaPercentY, crop.y + crop.height - 10);
          newCrop.width = Math.min(100 - newCrop.x, newWidth2);
          newCrop.height = Math.min(100 - newCrop.y, newHeight2);
          break;
          
        case 'bottomLeft':
          // Adjust x, width, height
          const newWidth3 = Math.max(10, crop.width - deltaPercentX);
          const newHeight3 = Math.max(10, crop.height + deltaPercentY);
          
          newCrop.x = Math.min(crop.x + deltaPercentX, crop.x + crop.width - 10);
          newCrop.width = Math.min(100 - newCrop.x, newWidth3);
          newCrop.height = Math.min(100 - newCrop.y, newHeight3);
          break;
          
        case 'bottomRight':
          // Adjust width, height
          newCrop.width = Math.max(10, Math.min(100 - newCrop.x, crop.width + deltaPercentX));
          newCrop.height = Math.max(10, Math.min(100 - newCrop.y, crop.height + deltaPercentY));
          break;
          
        default:
          break;
      }
    }

    // Update state and drag start position
    setCrop(newCrop);
    setDragStart({ x: clientX, y: clientY });
  };

  // Handle mouse up to end dragging
  const handleMouseUp = () => {
    setDraggingCorner(null);
    setDraggingBox(false);
  };

  // Calculate crop box display
  const cropPixels = cropToPixels();
  
  return (
    <div 
      ref={containerRef}
      className="crop-tool-container relative w-full max-w-full overflow-hidden rounded-lg"
      style={{ height: 'auto', maxHeight: '400px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      onMouseMove={handleMouseMove}
      onTouchMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onTouchEnd={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div className="relative">
        <img
          ref={imageRef}
          src={imageUrl}
          alt="Image to crop"
          className="max-w-full max-h-[350px] object-contain"
          style={{ transform: `rotate(${rotation}deg)` }}
          onLoad={handleImageLoad}
          draggable="false"
        />
        
        {imgLoaded && (
          <div 
            className="absolute cursor-move"
            style={{
              left: `${cropPixels.x}px`,
              top: `${cropPixels.y}px`,
              width: `${cropPixels.width}px`,
              height: `${cropPixels.height}px`,
              border: '2px dashed #fff',
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
            }}
            onMouseDown={(e) => handleMouseDown(e)}
            onTouchStart={(e) => handleMouseDown(e)}
          >
            {/* Top Left Corner */}
            <div 
              className="absolute w-4 h-4 bg-white border border-gray-800 rounded-full cursor-nwse-resize"
              style={{ top: '-8px', left: '-8px' }}
              onMouseDown={(e) => handleMouseDown(e, 'topLeft')}
              onTouchStart={(e) => handleMouseDown(e, 'topLeft')}
            />
            
            {/* Top Right Corner */}
            <div 
              className="absolute w-4 h-4 bg-white border border-gray-800 rounded-full cursor-nesw-resize"
              style={{ top: '-8px', right: '-8px' }}
              onMouseDown={(e) => handleMouseDown(e, 'topRight')}
              onTouchStart={(e) => handleMouseDown(e, 'topRight')}
            />
            
            {/* Bottom Left Corner */}
            <div 
              className="absolute w-4 h-4 bg-white border border-gray-800 rounded-full cursor-nesw-resize"
              style={{ bottom: '-8px', left: '-8px' }}
              onMouseDown={(e) => handleMouseDown(e, 'bottomLeft')}
              onTouchStart={(e) => handleMouseDown(e, 'bottomLeft')}
            />
            
            {/* Bottom Right Corner */}
            <div 
              className="absolute w-4 h-4 bg-white border border-gray-800 rounded-full cursor-nwse-resize"
              style={{ bottom: '-8px', right: '-8px' }}
              onMouseDown={(e) => handleMouseDown(e, 'bottomRight')}
              onTouchStart={(e) => handleMouseDown(e, 'bottomRight')}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CropTool;
